class VqlCarousel extends HTMLElement {
  constructor() {
    super();
    this.state = {
      position: 0,
      maxVisible: 0,
      maxScroll: 0,
      itemWidth: 0,
      itemGap: 0
    };
  }

  connectedCallback() {
    this.init();
  }

  /**
  * Returns the structured view model of elements inside the component
  */
  getViewModel() {
    return {
      $carousel: this.querySelector(".top-banner-hero__carousel-track"),
      $items: [...this.querySelectorAll(".image-and-text__link")],
      $prevArrow: this.querySelector(".prev"),
      $nextArrow: this.querySelector(".next"),
      $nextControls: this.querySelector(".top-banner-hero-control--next"),
      $prevControls: this.querySelector(".top-banner-hero-control--previous"),
    };
  }

  /**
  * Initializes the carousel, sets up event listeners, and calculates initial layout
  */
  init() {
    const { $prevArrow, $nextArrow } = this.getViewModel();

    if (!this.isValidCarousel()) return;

    $prevArrow.addEventListener("click", () => this.slideCarousel(-1));
    $nextArrow.addEventListener("click", () => this.slideCarousel(1));

    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') this.slideCarousel(-1);
      if (e.key === 'ArrowRight') this.slideCarousel(1);
    });

    this.resizeHandler = debounce(() => this.updateCarousel(), 100);
    window.addEventListener("resize", this.resizeHandler);

    this.updateCarousel();
  }

  /**
  * Validates if the required elements exist
  */
  isValidCarousel() {
    const { $carousel, $items, $prevArrow, $nextArrow } = this.getViewModel();
    return $carousel && $prevArrow && $nextArrow && $items.length > 0;
  }

  /**
  * Updates the carousel layout based on available space
  */
  updateCarousel() {
    if (window.innerWidth < 964) return;

    const { $carousel, $items } = this.getViewModel();
    this.state.itemGap = this.getSpacing();

    const containerWidth = this.offsetWidth;
    const itemWidth = $items[0].offsetWidth + this.state.itemGap;

    this.state.itemWidth = itemWidth;

    this.state.maxVisible = Math.floor(containerWidth / itemWidth);
    const totalWidth = $items.length * itemWidth;
    this.state.maxScroll = Math.max(0, Math.ceil((totalWidth - containerWidth) / itemWidth));

    if (this.unslick()) return;

    this.state.position = Math.max(0, Math.min(this.state.position, this.state.maxScroll));
    $carousel.style.transform = `translateX(-${this.state.position * itemWidth}px)`;

    this.updateArrows();
  }

  /**
  * Retrieves the global CSS variable `--spacing-rabbit` and converts it to pixels if needed.
  * @returns {number} Computed value of `--spacing-rabbit` in pixels.
  */
  getSpacing() {
    const rootStyle = getComputedStyle(document.documentElement);
    let spacing = rootStyle.getPropertyValue("--spacing-rabbit").trim();

    if (!spacing) return 10;

    const rootFontSize = parseFloat(rootStyle.fontSize);
    spacing = parseFloat(spacing) * rootFontSize;

    return spacing || 10;
  }

  /**
  * Moves the carousel left or right
  * @param {number} direction - Scroll direction (-1 for left, 1 for right)
  */
  slideCarousel(direction) {
    const { $carousel, $items } = this.getViewModel();
    const { itemWidth, itemGap } = this.state;

    if (this.state.maxVisible >= $items.length) return;

    this.state.position += direction;
    this.state.position = Math.max(0, Math.min(this.state.position, this.state.maxScroll));
    $carousel.style.transform = `translateX(-${this.state.position * itemWidth}px)`;
    this.updateArrows();
  }

  /**
  * Updates arrow visibility based on position
  */
  updateArrows() {
    const { $nextControls, $prevControls } = this.getViewModel();
    
    const hasScrolling = this.state.maxScroll > 0;

    $prevControls.classList.toggle("show", hasScrolling && this.state.position > 0);
    $nextControls.classList.toggle("show", hasScrolling && this.state.position < this.state.maxScroll);
  }

  /**
  * Disables the carousel if all items fit
  * @returns {boolean} - True if the carousel should be disabled
  */
  unslick() {
    const { $carousel, $items, $prevControls, $nextControls } = this.getViewModel();
    const containerWidth = this.offsetWidth;
    const totalWidth = $items.reduce((acc, item) => acc + item.offsetWidth + this.state.itemGap, 0);

    if (totalWidth <= containerWidth) {
      $carousel.style.transform = "translateX(0)";
      $prevControls.classList.remove("show");
      $nextControls.classList.remove("show");
      return true;
    }

    return false;
  }
}

customElements.define("vql-carousel", VqlCarousel);